import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Market, Subregion } from "models/regions"
import {
  createMarket,
  deleteMarket,
  deleteSubRegion,
  fetchMarkets,
  updateMarket,
  updateSubRegion,
} from "api/regions"
import { css, cx } from "@emotion/css"
import {
  marketsClass,
  openMarkets,
  subRegionClass,
  summaryClass,
  summaryMarkers,
  summaryTitle,
} from "./css"
//eslint-disable-next-line
import { queryCache, useMutation, useQuery } from "react-query"
import { useHistory, useParams } from "react-router"

import { ApiError } from "api/http/utils"
import { COLUMNS } from "./columns"
import { ClickAwayListener } from "@material-ui/core"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import MoreActions from "components/MoreActions"
import { Rable } from "components/Rable"
import { TextInput } from "components/inputs/TextInput"
import { WithAuthority } from "components/WithAuthority"
import { fetchUnassignedMembers } from "api/team"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useToggle } from "hooks/useToggle"

interface ISubRegionProps {
  subRegion: Subregion
}

function SubRegion(props: ISubRegionProps) {
  const { t } = useI18n()
  const history = useHistory()
  const { hasAuthority } = useAuthenticated()
  const { tab } = useParams<{ id: string; tab: string }>()
  const [open, setOpen] = useState<boolean>(false)
  const [onEdit, setOnEdit] = useState(false)
  const [errors, setErrors] = useState<any | undefined>(undefined)
  const [tableErrors, setTableErrors] = useState<string[] | undefined>(
    undefined,
  )
  const [subRegionErrors, setSubRegionErrors] = useState<string[] | undefined>(
    undefined,
  )
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const isModalOpen = Boolean(modalInfo)
  const [subRgModalInfo, setSubRgModalInfo] = useState<ModalInfo>()
  const isSubRgModalOpen = Boolean(subRgModalInfo)

  const newMarketDisable = () => (row: Partial<Market>) => {
    const { name, code } = row
    return !code || !name
  }

  const { data: markets, status } = useQuery(
    ["markets", props.subRegion.id],
    fetchMarkets,
  )

  const { data: unassigned, isFetching: isFetchingUnassigned } = useQuery(
    ["unassignedMembers"],
    fetchUnassignedMembers,
    { enabled: hasAuthority(["AUTH_VIEW_USER_UNASSIGNED"]) },
  )

  useEffect(() => {
    setTableErrors(undefined)
  }, [tab])

  const [_delete] = useMutation(deleteSubRegion, {
    onSuccess: () => {
      setSubRgModalInfo(undefined)
      queryCache.invalidateQueries("region")
      history.push(`/admin-regions/${tab}`)
    },
  })
  const [update] = useMutation(updateSubRegion, {
    onSuccess: () => {
      history.push(`/admin-regions/${tab}`)
      setOnEdit(false)
      setSubRegionErrors(undefined)
      setTableErrors(undefined)
    },
    onError: (error: ApiError) => {
      setSubRegionErrors(error.fields?.map((e: any) => e.key))
    },
  })

  const [remove] = useMutation(deleteMarket, {
    onSuccess: (_, id: string) => {
      setModalInfo(undefined)
      /*  state.removeRow(id) */
      setTableErrors(undefined)
      setOpen(false)
      queryCache.invalidateQueries("markets")
    },
    onError: (e) => {
      setModalInfo(undefined)
      setTableErrors([e.message])
    },
  })

  const [patch] = useMutation(updateMarket, {
    onSuccess: (d: Market) => {
      // do nothing
      // state.updateRow(d)
      setTableErrors(undefined)
      queryCache.invalidateQueries("markets")
    },
    onError: (error: ApiError) => {
      setTableErrors(error.fields?.map((e: any) => e.key))
    },
  })

  const handleDelete = useCallback(
    (rowId: string) => {
      setModalInfo({
        title: t("market.modal.delete.title"),
        type: "primary_destructive",
        description: t("market.modal.delete.description"),
        submitLabel: t("modal.label.delete"),
        onSubmit: async () => await remove(rowId || ""),
      })
    },
    [remove, t],
  )

  const handleSubRegionDelete = useCallback(
    (id: string) => {
      setSubRgModalInfo({
        title: t("subregion.modal.delete.title"),
        type: "primary_destructive",
        description: t("subregion.modal.delete.description"),
        submitLabel: t("modal.label.delete"),
        onSubmit: () => _delete(id),
      })
    },
    [_delete, t],
  )

  const onUpdate = useCallback(
    (rowId: string, row: Partial<Market>) => {
      patch({
        id: rowId,
        ...row,
      })
    },
    [patch],
  )
  const [create] = useMutation(createMarket, {
    onSuccess: (response: any) => {
      /*    state.addRow(response) */
      newRowToggle.onClose()
      setErrors(undefined)
      setOpen(false)
      queryCache.invalidateQueries("markets")
    },
    onError: (error: ApiError) => {
      setErrors(error.fields?.map((e: any) => e))
    },
  })
  const onCreate = (newRow: Partial<Market>) => {
    return create({ ...newRow, subRegionId: props.subRegion.id })
  }

  const removeErrors = () => setErrors(undefined)

  const ctx = useMemo(() => {
    return {
      unassigned,
      isFetchingUnassigned,
      removeErrors,
    }
  }, [unassigned, isFetchingUnassigned])

  const newRowCols = COLUMNS?.filter((c) => c.existsInCreate)
  const newRowToggle = useToggle()

  const isPending = status === "loading"

  const actionAllowed = hasAuthority([
    "AUTH_ACCESS_GLOBAL_FINANCE",
    "AUTH_ACCESS_MARKET_FINANCE",
    "AUTH_ACCESS_AGENCY_FINANCE",
  ])
  const required = COLUMNS?.filter((c) => c.required)

  return (
    <contextOptions.Provider value={ctx}>
      <div className={subRegionClass}>
        <div
          className={cx(summaryClass, {
            [styles.selectedMediaTypes]: open,
          })}
        >
          <WithAuthority allowed={["AUTH_VIEW_SUBREGION"]}>
            <ClickAwayListener onClickAway={() => setOnEdit(false)}>
              <div
                className={summaryTitle}
                onClick={() => {
                  hasAuthority(["AUTH_EDIT_SUBREGION"]) && setOnEdit(true)
                }}
              >
                {onEdit ? (
                  <TextInput
                    onBlur={(e) =>
                      e.currentTarget.value &&
                      update({
                        id: props.subRegion.id,
                        name: e.currentTarget.value,
                      })
                    }
                    defaultValue={props.subRegion.name}
                    size="small"
                  />
                ) : (
                  props.subRegion.name
                )}
              </div>
            </ClickAwayListener>
          </WithAuthority>
          <div style={{ display: "flex" }}>
            <WithAuthority allowed={["AUTH_VIEW_MARKET"]}>
              <div
                onClick={() => {
                  setOpen(!open)
                  setErrors(undefined)
                }}
                className={summaryMarkers}
              >
                <strong>{open ? "Hide Markets" : "Show Markets"}</strong>
              </div>
            </WithAuthority>
            {markets &&
            Boolean(markets.length < 1) &&
            hasAuthority(["AUTH_EDIT_MARKET"]) ? (
              <WithAuthority allowed={["AUTH_DELETE_SUBREGION"]}>
                <div className={moreActions}>
                  <MoreActions
                    options={[
                      // DELETE
                      {
                        label: t("subregions.list.delete.action"),
                        icon: <DeleteIcon />,
                        onClick: () => {
                          handleSubRegionDelete(props.subRegion.id)
                        },
                      },
                    ]}
                  />
                </div>
              </WithAuthority>
            ) : (
              <div className={emptySpace}></div>
            )}
          </div>
        </div>
        <div className={cx(marketsClass, { [openMarkets]: open })}>
          {(markets || [])?.length > 0 && (
            <div className={createMarkets}>
              {markets?.length}
              &nbsp;
              {"Markets"}
              <WithAuthority allowed={["AUTH_CREATE_MARKET"]}>
                <strong
                  className={cx(createNew, { [toggle]: newRowToggle.open })}
                  onClick={newRowToggle.onOpen}
                >
                  {"+ New Market"}
                </strong>
              </WithAuthority>
            </div>
          )}
          <WithAuthority allowed={["AUTH_VIEW_MARKET"]}>
            <Rable<Market>
              data={markets || []}
              columns={COLUMNS}
              required={required}
              createRow={onCreate}
              newRowColumns={newRowCols}
              updateRow={onUpdate}
              label="rate.card.loading"
              open={newRowToggle.open}
              onClose={newRowToggle.onClose}
              pending={isPending}
              onOpen={newRowToggle.onOpen}
              hasActions={actionAllowed}
              actions={(row: Market) => [
                {
                  label: t("actions.delete"),
                  icon: <DeleteIcon />,
                  hidden: false,
                  onClick: () => handleDelete(row?.id || ""),
                },
              ]}
              disabledCondition={newMarketDisable()}
              emptyState={
                !markets?.length &&
                !newRowToggle.open && (
                  <>
                    <div className={emptyMessage}>
                      {t("subregion.empty.market")}
                    </div>
                    <WithAuthority allowed={["AUTH_CREATE_MARKET"]}>
                      <div
                        onClick={newRowToggle.onOpen}
                        className={emptyMarket}
                      >
                        {t("subregion.empty.add.market")}
                      </div>
                    </WithAuthority>
                  </>
                )
              }
            />
          </WithAuthority>
          <div className={errorMessage}>
            {errors
              ? errors.map((error: any, idx: number) => (
                  <div key={idx}>
                    {t(error.name).toUpperCase()} : {t(error.key)}
                    <br />
                  </div>
                ))
              : ""}
            {tableErrors ? tableErrors.map((error: any) => t(error)) : ""}
          </div>
        </div>
        <div className={errorMessage}>
          {subRegionErrors ? subRegionErrors.map((error: any) => t(error)) : ""}
        </div>
        <ConfirmationModal
          open={isModalOpen}
          onCancel={() => setModalInfo(undefined)}
          {...modalInfo}
        />
        <ConfirmationModal
          open={isSubRgModalOpen}
          onCancel={() => setSubRgModalInfo(undefined)}
          {...subRgModalInfo}
        />
      </div>
    </contextOptions.Provider>
  )
}

export default SubRegion

export const contextOptions = createContext<any | undefined>(undefined)

export const useContextOptions = () => {
  const options = useContext(contextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  header: css`
    display: flex;
    justify-content: space-between;
    padding: 11px 103px 11px 43px;
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }

    .RebaseTable__cell.RebaseTable__grid-cell {
      display: flex;
      justify-content: center;
    }

    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }

      :last-child {
        display: flex;
        justify-content: center;
      }
    }

    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  selectedMediaTypes: css`
    border-radius: 4px;
    border: solid 1px var(--primary-main);
    background-color: var(--white);
  `,
}

const errorMessage = css`
  font-size: 13px;
  color: var(--light-red);
  white-space: pre-wrap;
`
const emptyMessage = css`
  margin: 27px 0px 8px 0px;
  font-size: 16px;
  font-weight: 300;
  text-align: center;
  color: var(--dark-grey);
`

const emptyMarket = css`
  font-size: 13px;
  font-weight: bold;
  text-align: center;
  color: var(--primary-main);
  cursor: pointer;
`
const emptySpace = css`
  width: 46px;
`

const createNew = css`
  font-size: 13px;
  color: var(--primary-main);
  margin-left: 35px;
  cursor: pointer;
`

const createMarkets = css`
  font-size: 13px;
  margin-bottom: 20px;
  margin-top: 20px;
`

const toggle = css`
  display: none;
`

const moreActions = css`
  width: 46px;
  display: flex;
  align-items: center;
`
